import { Button, Stack, Text, useToast } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { OnboardingLayout } from '../../components/OnboardingLayout';
import { Page } from '../../components/Page';
import { SimpleForm } from '../../components/SimpleForm';
import { SimpleInput } from '../../components/SimpleInput';
import { SimpleSignature } from '../../components/SimpleSignature';
import { trpc } from '../../config/trpc';
import { useCustomerCopy } from '../../hooks/tenant/useCustomerCopy';
import { useScreenView } from '../../hooks/useScreenView';
import { MixpanelEvent, trackEvent } from '../../utils/mx';

type FormValues = {
  title: string;
  signature: string;
};

const schema = z.object({
  title: z.string({ required_error: 'Title is required' }),
  signature: z.string({ required_error: 'Signature is required' }),
});

export const RegisterSignature = () => {
  const toast = useToast({ position: 'bottom', size: 'sm', duration: 3000 });
  const copy = useCustomerCopy();
  useScreenView(MixpanelEvent.SignatureView);

  const { mutateAsync: acceptTos, isLoading: isSubmitting } = trpc.acceptTos.useMutation();

  const methods = useForm<Partial<FormValues>>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
  });

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        trackEvent(MixpanelEvent.SignatureSubmit);
        await acceptTos({ title: values.title, signature: values.signature });

        toast({
          title: 'Success',
          description: 'All signed up!',
          status: 'success',
        });
        window.location.reload();
      } catch (error) {
        toast({
          title: 'Company could not be created',
          description: error.message,
          status: 'error',
        });
      }
    },
    [acceptTos, toast],
  );

  return (
    <Page title="Sign" showHeader={false} showFooter={false}>
      <OnboardingLayout heading={copy('register.signature.heading')} step={2}>
        <SimpleForm {...methods} onSubmit={handleSubmit}>
          <Stack>
            <Text textStyle="bodySmall">
              {copy('register.signature.termsIntro')}{' '}
              <Button as={Link} to="/terms" size="sm" variant="link" colorScheme="primary" target="_blank">
                Terms of Service
              </Button>{' '}
              and{' '}
              <Button
                as={Link}
                to={copy('legal.privacyUrl')}
                target="_blank"
                size="sm"
                variant="link"
                colorScheme="primary"
              >
                Privacy Policy
              </Button>
              .
            </Text>
            <Text textStyle="bodySmall">{copy('register.signature.clause1')}</Text>
            <Text textStyle="bodySmall">{copy('register.signature.clause2')}</Text>
            <Text textStyle="bodySmall">{copy('register.signature.clause3')}</Text>
          </Stack>
          <Stack>
            <SimpleInput name="title" variant="outline" label="Job Title" w="full" />
            <SimpleSignature name="signature" variant="outline" />
          </Stack>
          <Button colorScheme="primary" size="lg" alignSelf="flex-end" type="submit" isLoading={isSubmitting}>
            Continue
          </Button>
        </SimpleForm>
      </OnboardingLayout>
    </Page>
  );
};
